import { MARKER_DIAMETER } from "./metrics";

/** The space below a disc: as wide as the disc, for two lines of name and the POV badge. */
export const NAME_BOX_WIDTH_PX = MARKER_DIAMETER;
export const NAME_BOX_HEIGHT_PX = 88;

/** Clear space kept between a disc and anything else. */
const CLEARANCE_PX = 8;
const SEARCH_STEP_PX = 2;

export interface Seat {
  dx: number;
  dy: number;
}

/** Evenly around a circle, starting from the top. */
export function seatOnCircle(index: number, count: number, radius: number): Seat {
  const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
  return { dx: Math.cos(angle) * radius, dy: Math.sin(angle) * radius };
}

function discHitsName(disc: Seat, name: Seat): boolean {
  const half = MARKER_DIAMETER / 2;
  const left = name.dx - NAME_BOX_WIDTH_PX / 2;
  const right = name.dx + NAME_BOX_WIDTH_PX / 2;
  const top = name.dy + half;
  const bottom = top + NAME_BOX_HEIGHT_PX;
  const nearestX = Math.max(left, Math.min(disc.dx, right));
  const nearestY = Math.max(top, Math.min(disc.dy, bottom));
  return Math.hypot(disc.dx - nearestX, disc.dy - nearestY) < half + CLEARANCE_PX;
}

function namesOverlap(a: Seat, b: Seat): boolean {
  return Math.abs(a.dx - b.dx) < NAME_BOX_WIDTH_PX
    && Math.abs(a.dy - b.dy) < NAME_BOX_HEIGHT_PX;
}

/** No disc touches another disc or anyone's name at this size. */
export function circleIsClear(count: number, radius: number): boolean {
  const seats = Array.from({ length: count }, (_, index) => seatOnCircle(index, count, radius));
  return seats.every((seat, i) => seats.every((other, j) => {
    if (i === j) {
      return true;
    }
    const discsClear = Math.hypot(seat.dx - other.dx, seat.dy - other.dy)
      >= MARKER_DIAMETER + CLEARANCE_PX;
    return discsClear && !discHitsName(seat, other) && !namesOverlap(seat, other);
  }));
}

/** Rail showing between the lowest name and the rail itself. */
const RAIL_CLEARANCE_PX = 20;

const radiusByCount = new Map<number, number>();
const riseByCount = new Map<number, number>();

/** How far above the rail a group's center sits, so all of it, names included, clears the rail. */
export function groupRiseAboveRail(count: number): number {
  const known = riseByCount.get(count);
  if (known !== undefined) {
    return known;
  }
  const radius = markerCircleRadius(count);
  const lowestSeat = Math.max(
    0,
    ...Array.from({ length: Math.max(1, count) }, (_, index) => (
      count <= 1 ? 0 : seatOnCircle(index, count, radius).dy
    )),
  );
  const rise = lowestSeat + MARKER_DIAMETER / 2 + NAME_BOX_HEIGHT_PX + RAIL_CLEARANCE_PX;
  riseByCount.set(count, rise);
  return rise;
}

/** The tightest circle where every name stays readable, so small groups take less room. */
export function markerCircleRadius(count: number): number {
  if (count <= 1) {
    return 0;
  }
  const known = radiusByCount.get(count);
  if (known !== undefined) {
    return known;
  }
  let radius = MARKER_DIAMETER / 2;
  while (!circleIsClear(count, radius)) {
    radius += SEARCH_STEP_PX;
  }
  radiusByCount.set(count, radius);
  return radius;
}
